import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, Trophy, CaretDown } from "@phosphor-icons/react";
import { useStatsBus } from "@/lib/statsBus";

/**
 * Widget: "Mis mejores duplas" — best pairings a given player has produced,
 * grouped by a rolling time window (week/month/year/all).
 *
 * Props:
 *   userId: string             — user whose pairs we want to see
 *   userName?: string          — display name (optional)
 *   defaultPeriod?: 'week'|'month'|'year'|'all'   — default rolling window
 *   compact?: boolean          — if true, renders a slimmer card
 *   testid?: string
 */
export default function BestPairs({
  userId,
  userName = "",
  defaultPeriod = "week",
  compact = false,
  testid = "best-pairs",
}) {
  const [period, setPeriod] = useState(defaultPeriod);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api
      .get(`/players/${userId}/best-pairs?period=${period}&top=5`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId, period]);

  const periodLabel = {
    week: "Esta semana",
    month: "Últimos 30 días",
    year: "Este año",
    all: "Histórico",
  };

  return (
    <div
      className={`card-tactical ${compact ? "p-4" : "p-5"} border-amber-500/25`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-amber-400 font-semibold">
            <Trophy size={11} weight="fill" className="inline mr-1" /> Mejores duplas
          </p>
          <h3 className="font-['Barlow_Condensed'] font-black text-lg uppercase text-white leading-tight">
            {userName || "Jugador"}
          </h3>
        </div>
        <div className="relative">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="appearance-none bg-black/60 border border-white/15 text-[11px] uppercase tracking-widest px-3 py-1.5 pr-7 text-amber-300 focus:border-amber-400 focus:outline-none cursor-pointer"
            data-testid={`${testid}-period`}
          >
            <option value="week">Semana</option>
            <option value="month">Mes</option>
            <option value="year">Año</option>
            <option value="all">Histórico</option>
          </select>
          <CaretDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 text-amber-400 pointer-events-none" />
        </div>
      </div>

      {loading ? (
        <div className="h-16 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent animate-spin" />
        </div>
      ) : !data || !data.best_pairs || data.best_pairs.length === 0 ? (
        <p className="text-[11px] text-gray-500" data-testid={`${testid}-empty`}>
          Sin partidos validados en <b>{periodLabel[period]?.toLowerCase()}</b>.
        </p>
      ) : (
        <>
          <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-2" data-testid={`${testid}-meta`}>
            {periodLabel[period]} · {data.total_pairs} pareja{data.total_pairs === 1 ? "" : "s"}
          </p>
          <div className="space-y-1.5" data-testid={`${testid}-list`}>
            {data.best_pairs.map((p, i) => {
              const winRate = p.matches > 0 ? Math.round((p.wins / p.matches) * 100) : 0;
              const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`;
              return (
                <div
                  key={p.partner_id || `guest-${p.partner_name}-${i}`}
                  className="flex items-center gap-2 py-1.5 px-2 bg-black/40 border border-white/5 rounded"
                  data-testid={`${testid}-row-${i}`}
                >
                  <span className="text-sm w-8 shrink-0 text-center">{medal}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 text-xs text-white">
                      <Users size={11} className="text-amber-400 shrink-0" />
                      <span className="truncate">{userName || "Tú"}</span>
                      <span className="text-amber-400 font-black shrink-0" aria-hidden="true">·</span>
                      <span className="truncate font-semibold">{p.partner_name}</span>
                    </div>
                    <p className="text-[9px] text-gray-500 uppercase tracking-widest font-['JetBrains_Mono']">
                      {p.matches}p · {p.wins}v · {winRate}%
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-['Barlow_Condensed'] font-black text-lg leading-none text-amber-400">{p.points}</p>
                    <p className="text-[8px] text-gray-500 uppercase tracking-widest">pts</p>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
